import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  X,
  Layers,
  Handshake,
  Clock,
  Wallet,
  Building2,
  Store,
  FileText,
  CheckCircle2,
  Calendar,
  Eye,
  ArrowLeft,
} from "lucide-react";
import { useRef, useState } from "react";

export interface PdvInvestmentsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatBRL = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

const summary = [
  { label: "Quantidade de investimentos", value: "14", icon: Layers },
  { label: "Valor de Investimentos Negociados", value: formatBRL(90), icon: Handshake, highlight: false },
  { label: "Valor de Investimentos Aguardando Performance", value: formatBRL(0), icon: Clock, highlight: false },
  { label: "Valor Recebido", value: formatBRL(112770), icon: Wallet, highlight: true },
];

const investments = [
  {
    tipo: "Investimento Promocional",
    subtipo: "% de Compra / PDV+",
    grupo: "GENÉRICOS",
    valor: "R$ 10",
    inicio: "01/03/2022",
    fim: "30/04/2022",
    status: "Negociado",
    acao: "Visualizar",
  },
  {
    tipo: "Investimento Promocional",
    subtipo: "% de Compra / PDV+",
    grupo: "GENÉRICOS",
    valor: "R$ 10",
    inicio: "03/03/2021",
    fim: "18/05/2021",
    status: "Aceito",
    acao: "Aceito",
  },
  {
    tipo: "Investimento Promocional",
    subtipo: "% de Compra / PDV+",
    grupo: "GENÉRICOS",
    valor: "R$ 10",
    inicio: "01/04/2021",
    fim: "18/05/2021",
    status: "Aceito",
    acao: "Aceito",
  },
  {
    tipo: "Investimento Promocional",
    subtipo: "% de Compra / PDV+",
    grupo: "GENÉRICOS",
    valor: "R$ 10",
    inicio: "01/06/2021",
    fim: "31/07/2021",
    status: "Aceito",
    acao: "Aceito",
  },
  {
    tipo: "Investimento Promocional",
    subtipo: "% de Compra / PDV+",
    grupo: "GENÉRICOS",
    valor: "R$ 10",
    inicio: "01/08/2021",
    fim: "30/09/2021",
    status: "Aceito",
    acao: "Aceito",
  },
  {
    tipo: "Investimento Promocional",
    subtipo: "% de Compra / PDV+",
    grupo: "GENÉRICOS",
    valor: "R$ 10",
    inicio: "03/03/2021",
    fim: "18/05/2021",
    status: "Aceito",
    acao: "Aceito",
  },
];

export function PdvInvestmentsModal({
  open,
  onOpenChange,
}: PdvInvestmentsModalProps) {
  const [termoOpen, setTermoOpen] = useState(false);
  const [reciboOpen, setReciboOpen] = useState(false);
  const guardCloseRef = useRef(false);
  const childOpen = termoOpen || reciboOpen;

  const guardOnClose = (next: boolean, setter: (v: boolean) => void) => {
    if (!next) {
      guardCloseRef.current = true;
      window.setTimeout(() => {
        guardCloseRef.current = false;
      }, 350);
    }
    setter(next);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => {
          if (childOpen || guardCloseRef.current) e.preventDefault();
        }}
        onFocusOutside={(e) => {
          if (childOpen || guardCloseRef.current) e.preventDefault();
        }}
        className="w-full max-w-6xl overflow-hidden rounded-xl border-none p-0 shadow-2xl [&>button]:hidden"
      >
        <div className="relative flex items-center justify-between bg-tudo-blue px-5 py-3">
          <DialogTitle className="text-left text-sm font-bold uppercase tracking-wide text-white">
            Investimentos no PDV
          </DialogTitle>
          <button
            onClick={() => onOpenChange(false)}
            className="text-white/80 transition-colors hover:text-white"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto p-6">
          <h2 className="mb-6 text-center text-3xl font-semibold text-tudo-blue">
            Investimentos
          </h2>

          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {summary.map((card, idx) => {
              const CardIcon = card.icon;
              return (
                <div
                  key={idx}
                  style={{ animationDelay: `${idx * 70}ms` }}
                  className={`group relative flex animate-in fade-in slide-in-from-bottom-2 flex-col overflow-hidden rounded-xl border p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${
                    card.highlight
                      ? "border-transparent bg-gradient-to-br from-tudo-blue to-tudo-blue/80 text-white hover:shadow-tudo-blue/30"
                      : "border-gray-200 bg-white hover:border-tudo-blue/40 hover:shadow-tudo-blue/10"
                  }`}
                >
                  {card.highlight && (
                    <div className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-white/10 blur-xl" />
                  )}
                  <div
                    className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg transition-transform duration-200 group-hover:scale-110 ${
                      card.highlight
                        ? "bg-white/15 ring-1 ring-white/25"
                        : "bg-tudo-blue/10"
                    }`}
                  >
                    <CardIcon
                      className={`h-5 w-5 ${card.highlight ? "text-white" : "text-tudo-blue"}`}
                    />
                  </div>
                  <span
                    className={`text-sm ${card.highlight ? "text-white/80" : "text-gray-600"}`}
                  >
                    {card.label}
                  </span>
                  <span
                    className={`mt-1 text-2xl font-bold ${card.highlight ? "text-white" : "text-tudo-blue"}`}
                  >
                    {card.value}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-tudo-blue text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">
                    Tipo de Investimento
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    Subtipo de Investimento
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    Grupo de Produtos
                  </th>
                  <th className="px-4 py-3 text-left font-medium">Valor</th>
                  <th className="px-4 py-3 text-left font-medium">
                    Vigência Inicial
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    Vigência Final
                  </th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {investments.map((inv, idx) => (
                  <tr key={idx} className="bg-white transition-colors even:bg-gray-50 hover:bg-tudo-blue/5">
                    <td className="px-4 py-3">{inv.tipo}</td>
                    <td className="px-4 py-3">{inv.subtipo}</td>
                    <td className="px-4 py-3">{inv.grupo}</td>
                    <td className="px-4 py-3 font-semibold text-tudo-blue">{inv.valor}</td>
                    <td className="px-4 py-3">{inv.inicio}</td>
                    <td className="px-4 py-3">{inv.fim}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                          inv.status === "Aceito"
                            ? "bg-tudo-green/10 text-tudo-green"
                            : "bg-tudo-orange/10 text-tudo-orange"
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            inv.status === "Aceito" ? "bg-tudo-green" : "bg-tudo-orange"
                          }`}
                        />
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {inv.acao === "Visualizar" ? (
                        <button
                          onClick={() => setTermoOpen(true)}
                          className="inline-flex w-32 items-center justify-center gap-1.5 rounded-md bg-tudo-green px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-tudo-green/80"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Visualizar
                        </button>
                      ) : (
                        <button
                          onClick={() => setReciboOpen(true)}
                          className="inline-flex w-32 items-center justify-center gap-1.5 rounded-md bg-tudo-blue px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-tudo-blue/80"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Aceito
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <TermoInvestimentoModal open={termoOpen} onOpenChange={(v) => guardOnClose(v, setTermoOpen)} />
    <ReciboModal open={reciboOpen} onOpenChange={(v) => guardOnClose(v, setReciboOpen)} />
    </>
  );
}

function InfoCard({
  title,
  icon: Icon,
  children,
  className,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`overflow-hidden rounded-xl border border-border bg-card shadow-sm ${className ?? ""}`}>
      <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2.5">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-tudo-blue/10">
          <Icon className="h-3.5 w-3.5 text-tudo-blue" />
        </div>
        <h3 className="text-xs font-bold uppercase tracking-wide text-tudo-blue">
          {title}
        </h3>
      </div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 p-4 sm:grid-cols-2">
        {children}
      </dl>
    </section>
  );
}

function Field({
  label,
  value,
  full = false,
}: {
  label: string;
  value?: string;
  full?: boolean;
}) {
  return (
    <div className={`flex flex-col gap-0.5 ${full ? "sm:col-span-2" : ""}`}>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="break-words text-sm font-medium text-foreground">
        {value && value.trim() ? value : <span className="text-muted-foreground/50">—</span>}
      </dd>
    </div>
  );
}

function TermoInvestimentoModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden">
        {/* Header */}
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
          <div className="relative flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                  Termo de Investimento
                </DialogTitle>
                <p className="truncate text-xs font-medium text-white/70">
                  Código 500 • Investimento Promocional
                </p>
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-muted/30 p-4 sm:p-5">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <InfoCard title="Empresa Investidora" icon={Building2}>
              <Field label="Empresa" value="Tudofarma Servicos de Tecnologia Ltda" full />
              <Field label="Endereço" value="Rod. Pres. Dutra-Pista Lateral, s/n" full />
              <Field label="Bairro" value="Porto da Igreja" />
              <Field label="Cidade" value="Guarulhos" />
              <Field label="UF" value="SP" />
              <Field label="CEP" value="07034-904" />
              <Field label="CNPJ / MF" value="21.565.281/0001-08" />
              <Field label="Inscrição Estadual" value="" />
            </InfoCard>

            <InfoCard title="Empresa Investida" icon={Store}>
              <Field label="Razão social" value="FCIA A POPULAR DE CORREAS LTDA" full />
              <Field label="Nome Fantasia" value="" />
              <Field label="Cidade" value="PETROPOLIS-RJ" />
              <Field label="UF" value="RJ" />
              <Field label="CEP" value="" />
              <Field label="CNPJ/MF" value="12.105.236/0001-71" />
              <Field label="Responsável" value="" />
              <Field label="Cargo" value="" />
              <Field label="Telefone" value="" />
              <Field label="e-mail" value="" />
            </InfoCard>
          </div>

          <InfoCard title="Dados do Investimento" icon={Layers}>
            <Field label="Código de Investimento" value="500" />
            <Field label="Solicitante" value="RENATA PEREIRA DE LIMA" />
            <Field label="Tipo de Investimento" value="Investimento Promocional" />
            <Field label="Subtipo de Investimento" value="% de Compra / PDV+" />
            <Field label="Vigência do Investimento" value="01/03/2022 até 30/04/2022" />
            <Field label="Natureza" value="Percentual" />
            <Field label="Valor / % / Qtd Acordado (a)" value="R$ 10" />
            <Field label="Esfera" value="Unidade de Negocio" />
            <Field label="Produtos" value="1003" />
            <Field label="Descrição" value="GENERICOS" />
            <Field label="CNPJ(s) Participantes" value="" full />
          </InfoCard>

          <InfoCard title="Contrapartida(s) Acordada(s)" icon={Handshake}>
            <Field label="Incremento de Demanda" value="R$ 0" />
            <Field label="Prazo" value="2 Meses" />
            <Field label="Demais Contrapartidas" value="" full />
          </InfoCard>

          <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4 text-tudo-blue/70" />
              <span>
                Emitido em <span className="font-semibold text-foreground">18 de junho de 2026</span>
              </span>
            </div>
            <span className="text-xs text-muted-foreground">CNPJ/MF: 21.565.281/0001-08</span>
          </div>

          <p className="rounded-xl border border-tudo-blue/20 bg-tudo-blue/5 px-4 py-3 text-sm font-medium text-foreground">
            O pagamento do investimento supracitado se dará mediante cumprimento de contrapartida(s) acordada(s) e assinatura eletrônica do(s) recibo(s).
          </p>

          {/* Tabela de produtos */}
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="bg-gradient-to-r from-tudo-blue to-tudo-blue/80 px-4 py-2.5 text-center text-sm font-bold uppercase tracking-wide text-white">
              Genéricos
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Código Produto
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Nome
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-tudo-blue/5">
                    <td className="px-4 py-2.5 font-semibold text-tudo-blue">1003</td>
                    <td className="px-4 py-2.5 text-foreground">GENERICOS</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer com ações */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-card px-4 py-3 sm:px-5">
          <button
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:border-tudo-blue hover:text-tudo-blue"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </button>
          <button
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-tudo-green to-tudo-green/85 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            <CheckCircle2 className="h-4 w-4" />
            Aceitar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReciboModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-2xl overflow-hidden rounded-xl border-none p-0 shadow-2xl [&>button]:hidden">
        <div className="relative flex items-center justify-between bg-tudo-blue px-5 py-3">
          <DialogTitle className="text-left text-sm font-bold uppercase tracking-wide text-white">
            Recibo
          </DialogTitle>
          <button
            onClick={() => onOpenChange(false)}
            className="text-white/80 transition-colors hover:text-white"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto bg-white p-8">
          <h2 className="mb-6 text-center text-3xl font-semibold uppercase tracking-wide text-tudo-blue">
            Recibo
          </h2>

          <div className="space-y-4 text-sm text-gray-800">
            <p>
              Recebemos do <strong>Tudofarma Servicos de Tecnologia Ltda</strong>,{" "}
              <strong>21.565.281/0001-08</strong>, a importância de{" "}
              <strong>R$ 1,63</strong> / UM REAL E SESSENTA E TRÊS CENTAVOS,
              referente a , para <strong>GENERICOS</strong> no período de{" "}
              <strong>01/11/2018</strong> a <strong>30/11/2018</strong>.
            </p>

            <p>
              <strong>CNPJ:</strong> 12.105.236/0001-71
            </p>

            <p>
              <strong>PETROPOLIS, 18 de junho de 2026</strong>
            </p>

            <p className="text-xs text-gray-600">
              *Este recibo é válido somente mediante depósito bancário.
            </p>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={() => onOpenChange(false)}
              className="rounded-md bg-tudo-blue px-6 py-2 text-sm font-medium text-white hover:bg-tudo-blue/80"
            >
              Voltar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
